import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
  Form,
  Button,
  ExternalLink,
  FieldCheckbox,
  FieldBirthdayInput,
  EditListingActionButton,
} from '../../components';
import { required } from '../../util/validators';

import css from './EditListingDOMForm.css';

const NOW = new Date();

const dateIsValid = value => {
  const { day, month, year } = value || {};
  const date = new Date(year, month - 1, day);
  const timeStamp = date.getTime();
  if (!isNaN(timeStamp) && timeStamp < NOW.getTime()) {
    return true;
  }
  return false;
};

export const EditListingDOMFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        onBackWithoutSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        category,
        isPublished,
        values,
        form,
      } = formRenderProps;

      const domLabelMessage = intl.formatMessage({
        id: 'EditListingDOMForm.domLabel',
      });
      const domPlaceholderMessage = intl.formatMessage({
        id: 'EditListingDOMForm.domPlaceholder',
      });
      const domRequiredMessage = intl.formatMessage({
        id: 'EditListingDOMForm.domRequired',
      });

      const { updateListingError, showListingsError } = fetchErrors || {};
      const errorMessage = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDOMForm.updateFailed" />
        </p>
      ) : null;
      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDOMForm.showListingFailed" />
        </p>
      ) : null;

      const invalidDate = intl.formatMessage(
        { id: 'EditListingDOMForm.invalidDate' },
        { day: NOW.toDateString() }
      );
      const completeLaterLabel = intl.formatMessage({
        id: 'EditListingDOMForm.completeLaterLabel',
      });
      const dayLabel = intl.formatMessage({ id: 'EditListingDOMForm.dayLabel' });
      const monthLabel = intl.formatMessage({ id: 'EditListingDOMForm.monthLabel' });
      const yearLabel = intl.formatMessage({ id: 'EditListingDOMForm.yearLabel' });

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;
      const dateOfManufacture = values && values.dateOfManufacture;
      const { day, month, year } = dateOfManufacture || {};
      const valueFromForm = new Date(year, month - 1, day);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessage}
          {errorMessageShowListing}

          <FieldCheckbox label={completeLaterLabel} id="domCompleteLater" name="domCompleteLater" />

          <p className={css.description}>
            <FormattedMessage
              id="EditListingDOMForm.completeLaterDescription"
              values={{
                link: (
                  <ExternalLink
                    href="https://kindershare.com/blog/2018/09/07/car_seat_date/"
                    target="_blank"
                  >
                    <FormattedMessage id="EditListingDOMForm.clickHere" />
                  </ExternalLink>
                ),
              }}
            />
          </p>

          <div className={css.domLabelMessage}>{domLabelMessage}</div>
          <FieldBirthdayInput
            id="dateOfManufacture"
            name="dateOfManufacture"
            label={dayLabel}
            labelForMonth={monthLabel}
            labelForYear={yearLabel}
            className={css.dateOfManufacture}
            valueFromForm={dateIsValid(dateOfManufacture) ? valueFromForm : null}
            validate={(value, allValues) => {
              if (category && category === 'carSeats') {
                if (isPublished) {
                  if (!value) {
                    return domRequiredMessage;
                  }

                  if (!dateIsValid(value)) {
                    return invalidDate;
                  }
                }

                const domCompleteLater = allValues && allValues.domCompleteLater;

                if (!domCompleteLater) {
                  return required(domRequiredMessage)(value);
                }
              }
              return undefined;
            }}
          />

          <EditListingActionButton
            invalid={invalid}
            nextBtnMessage={saveActionMsg}
            nextBtnClassName={css.submitButton}
            form={form}
            onBackWithoutSubmit={onBackWithoutSubmit}
            submitInProgress={submitInProgress}
            submitReady={submitReady}
            submitDisabled={submitDisabled}
          />
        </Form>
      );
    }}
  />
);

EditListingDOMFormComponent.defaultProps = {
  selectedPlace: null,
  updateError: null,
};

EditListingDOMFormComponent.propTypes = {
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  selectedPlace: propTypes.place,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingDOMFormComponent);
